import React, { useState, useEffect, Fragment } from 'react';
import { Modal, Form, Space, Button, Typography, Row, Steps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AgentNameInput from './AgentNameInput/AgentNameInput';
import KnowledgeBaseSection from './KnowledgeBaseSection/KnowledgeBaseSection';
import PlatformSection from './PlatformSection/PlatformSection';
import { PLATFORM_MAPPER } from './constants';
import { useFacebookSDK } from '../../../Hooks/useFacebookSDK';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import AdvancedControl from './AdvancedControl/AdvancedControl';
import './AddAgent.css';

const { Step } = Steps;
const { Title } = Typography;

const STEP_VALIDATION_FIELDS = [
    ['name'],
    ['knowledgeBase'],
    ['type', 'page'],
    [] // Advanced controls (no required fields)
];

const KNOWLEDGE_BASE_STEP_INDEX = 1;

const AddAgentModal = ({ onClose, onCreateAgent, mode = 'create', initialData = null }) => {
    const { t } = useTranslation();
    const { loading } = useGlobal();
    const [state, setState] = useState('');
    const switchFacebookApp = useFacebookSDK(2);
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [isMobile, setIsMobile] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [submitDisabled, setSubmitDisabled] = useState(mode !== 'edit');
    const [continueDisabled, setContinueDisabled] = useState(true);

    const hasValidationErrors = () => {
        const currentFields = STEP_VALIDATION_FIELDS[currentStep];
        if (!currentFields.length) return false; // For steps with no required fields

        return !!form.getFieldsError(currentFields)
            .filter(({ errors }) => errors.length).length;
    };

    const hasRequiredFields = () => {
        const currentFields = STEP_VALIDATION_FIELDS[currentStep];
        if (!currentFields.length) return true; // For steps with no required fields

        return currentFields.every(field => {
            const value = form.getFieldValue(field);
            switch (field) {
                case 'knowledgeBase':
                    // Handle different formats of knowledgeBase value
                    if (typeof value === 'object') {
                        return value && value.id;
                    } else if (typeof value === 'string') {
                        return value.length > 0;
                    } else if (mode === 'edit' && initialData) {
                        // In edit mode, check if initialData has knowledge base info
                        return !!(initialData.knowledge_base_id ||
                            (initialData.knowledge_base && initialData.knowledge_base.id));
                    }
                    return false;
                case 'type':
                case 'page':
                    return value && value.length > 0;
                default:
                    return value !== undefined && value !== null && value !== '';
            }
        });
    };

    const isButtonDisabled = () => {
        if (currentStep === 3) return false; // Advanced step is always enabled

        switch (currentStep) {
            case 0:
                return !form.getFieldValue('name');
            case KNOWLEDGE_BASE_STEP_INDEX:
                const knowledgeBase = form.getFieldValue('knowledgeBase');
                return !knowledgeBase || !knowledgeBase.id;
            case 2: // Platform step
                const type = form.getFieldValue('type');
                const page = form.getFieldValue('page');
                return !type || !page;
            default:
                return hasValidationErrors() || !hasRequiredFields();
        }
    };

    useEffect(() => {
        const checkIfMobile = () => setIsMobile(window.innerWidth <= 768);
        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Set initial button state for edit mode
    useEffect(() => {
        if (mode === 'edit') {
            // Define the function inside the effect to avoid dependency issues
            const checkButtonDisabled = () => {
                if (currentStep === 3) return false; // Advanced step is always enabled

                switch (currentStep) {
                    case KNOWLEDGE_BASE_STEP_INDEX:
                        const knowledgeBase = form.getFieldValue('knowledgeBase');
                        return !knowledgeBase || !knowledgeBase.id;
                    case 2: // Platform step
                        const type = form.getFieldValue('type');
                        const page = form.getFieldValue('page');
                        // Handle both normal string values and labelInValue object format
                        return !type || !page || (typeof page === 'object' && !page.value);
                    default:
                        return hasValidationErrors() || !hasRequiredFields();
                }
            };

            setSubmitDisabled(checkButtonDisabled());
        }
    }, [mode, currentStep, form, hasRequiredFields, hasValidationErrors]);

    // Update continueDisabled state when currentStep changes or form values change
    useEffect(() => {
        setContinueDisabled(isButtonDisabled());
    }, [currentStep, form.getFieldsValue()]);

    const handleCancel = async () => {
        form.resetFields();
        await switchFacebookApp(1);
        onClose();
    };

    const handleSubmit = async () => {
        try {
            // Validate all fields regardless of mode
            await form.validateFields();

            const values = form.getFieldsValue(true);

            // Extract page ID from the page object (if using labelInValue)
            const pageId = values.page?.value || values.page;

            // Determine the knowledge base ID based on form values or initial data
            let knowledgeBaseId;

            if (typeof values.knowledgeBase === 'object' && values.knowledgeBase?.id) {
                // Format: { id: "123", name: "KB Name" }
                knowledgeBaseId = values.knowledgeBase.id;
            } else if (typeof values.knowledgeBase === 'string' && values.knowledgeBase) {
                // Format: "123"
                knowledgeBaseId = values.knowledgeBase;
            } else if (mode === 'edit' && initialData?.knowledge_base_id) {
                // Fallback to initialData if available in edit mode
                knowledgeBaseId = initialData.knowledge_base_id;
            } else if (mode === 'edit' && initialData?.knowledge_base?.id) {
                // Alternative object format in initialData
                knowledgeBaseId = initialData.knowledge_base.id;
            }

            // Ensure we have a knowledge base ID for submission
            if (mode === 'edit' && !knowledgeBaseId) {
                throw new Error('Knowledge base ID is required');
            }

            const submissionValues = {
                ...initialData,
                name: values.name,
                system_instruction: values.system_instruction,
                knowledge_base_id: knowledgeBaseId,
                process_audio: values.analyzeAudio,
                process_document: true,
                process_image: values.analyzeImage,
                process_video: values.analyzeVideo,
                is_consumption_limit_enabled: values.is_consumption_limit_enabled,
                ...(values.is_consumption_limit_enabled ? {
                    max_coins_consumption: values.max_coins_consumption,
                    consumption_duration: values.consumption_duration,
                } : {}),
                is_processing_delay_enabled: values.is_processing_delay_enabled,
                ...(values.is_processing_delay_enabled ? {
                    processing_delay: values.processing_delay,
                    processing_jitter: values.processing_jitter,
                } : {}),
                snooze_duration: values.snooze_duration,
                platform_account: {
                    platform_type: PLATFORM_MAPPER[values.type],
                    facebook_page_id: pageId,  // Use the extracted pageId here
                },
                admins: values.admins,
            };

            setState('submitting');
            onCreateAgent(submissionValues, () => setState(''));
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const steps = [
        {
            title: t('agent_info'),
            content: <AgentNameInput />
        },
        {
            title: t('knowledge'),
            content: <KnowledgeBaseSection
                initialData={initialData}
                form={form}
                navigate={navigate}
                onUpdate={() => setSubmitDisabled(isButtonDisabled())}
            />
        },
        {
            title: t('platform'),
            content: <PlatformSection
                form={form}
                mode={mode}
                id={initialData?.id}
                initialData={initialData}
            />
        },
        {
            title: t('advanced'),
            content: <AdvancedControl />
        },
    ];

    const next = async () => {
        try {
            if (STEP_VALIDATION_FIELDS[currentStep].length > 0) {
                await form.validateFields(STEP_VALIDATION_FIELDS[currentStep]);
            }
            setCurrentStep(current => Math.min(current + 1, steps.length - 1));
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const handleValuesChange = (changedValues) => {
        if (currentStep === 3) {
            setSubmitDisabled(false);
            return;
        }

        if (mode === 'edit') {
            // In edit mode, validate all fields
            form.validateFields()
                .then(() => {
                    setSubmitDisabled(isButtonDisabled());
                    setContinueDisabled(isButtonDisabled());
                })
                .catch(() => {
                    setSubmitDisabled(isButtonDisabled());
                    setContinueDisabled(isButtonDisabled());
                });
            return;
        }

        // Create mode logic (unchanged)
        if ('knowledgeBase' in changedValues || 'type' in changedValues || 'page' in changedValues) {
            form.validateFields(STEP_VALIDATION_FIELDS[currentStep])
                .then(() => {
                    setSubmitDisabled(isButtonDisabled());
                    setContinueDisabled(isButtonDisabled());
                })
                .catch(() => {
                    setSubmitDisabled(true);
                    setContinueDisabled(true);
                });
        } else {
            const buttonDisabled = isButtonDisabled();
            setSubmitDisabled(buttonDisabled);
            setContinueDisabled(buttonDisabled);
        }
    };

    const prev = () => setCurrentStep(current => Math.max(current - 1, 0));

    const onStepClick = async (step) => {
        if (mode === 'edit' || step <= currentStep) {
            setCurrentStep(step);
        }
    };

    return (
        <Modal
            size={isMobile ? 'small' : 'default'}
            title={
                <div style={{ margin: 0 }}>
                    <Title level={4} style={{ margin: '0 0 16px 0' }}>
                        {mode === 'edit' ? t('edit_agent') : t('new_agent')}
                    </Title>
                    <Steps
                        current={currentStep}
                        size="small"
                        onChange={onStepClick}
                        direction={isMobile ? 'vertical' : 'horizontal'}
                        style={{
                            marginBottom: 0,
                            position: 'sticky',
                            top: 0,
                            backgroundColor: '#fff',
                            zIndex: 1,
                            padding: '8px 0',
                            paddingBottom: 0,
                        }}
                    >
                        {steps.map((step, index) => (
                            <Step
                                key={index}
                                title={step.title}
                                disabled={mode !== 'edit' && index > currentStep}
                            />
                        ))}
                    </Steps>
                </div>
            }
            open
            onCancel={handleCancel}
            footer={<Space style={{ justifyContent: 'space-between' }}>
                <div>
                    {currentStep > 0 && (
                        <Button onClick={prev} style={{ marginInlineEnd: 8 }}>
                            {t('back')}
                        </Button>
                    )}
                </div>

                <div>
                    {mode === 'edit' ? (
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            loading={state === 'submitting' && loading}
                            disabled={submitDisabled}
                        >
                            {t('update_agent')}
                        </Button>
                    ) : currentStep < steps.length - 1 ? (
                        <Button
                            type="primary"
                            onClick={next}
                            disabled={continueDisabled}
                        >
                            {t('continue')}
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            autoFocus
                            onClick={handleSubmit}
                            disabled={submitDisabled}
                            loading={state === 'submitting' && loading}
                        >
                            {t('create_agent')}
                        </Button>
                    )}
                </div>
            </Space>}
            width={isMobile ? "100%" : "720px"}
            style={isMobile ? {
                top: 0,
                margin: 0,
                maxWidth: '100%',
                height: '100vh',
            } : {}}
            wrapClassName="custom-modal-wrap"
            styles={{
                header: {
                    padding: '16px 24px',
                    marginBottom: 0,
                    borderBottom: '1px solid #f0f0f0',
                },
                body: {
                    height: isMobile ? 'unset' : '40vh',
                    overflowY: 'auto',
                    padding: '16px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    marginTop: 0,
                },
                footer: {
                    marginTop: 0,
                    paddingTop: 10,
                    boxSizing: 'border-box',
                },
                wrapper: {
                    overflowY: 0,
                }
            }}
        >
            <Form
                form={form}
                layout="vertical"
                name="add_agent_form"
                preserve={true}
                initialValues={mode === 'edit' ? {
                    name: initialData.name,
                    system_instruction: initialData.system_instruction,
                    knowledgeBase: initialData.knowledge_base_id
                        ? { id: initialData.knowledge_base_id }
                        : initialData.knowledge_base
                            ? { id: initialData.knowledge_base.id, name: initialData.knowledge_base.name }
                            : undefined,
                    type: Object.keys(PLATFORM_MAPPER)[Object.values(PLATFORM_MAPPER).indexOf(initialData.platform_account.platform_type)],
                    page: initialData.platform_account.facebook_page_id,
                    analyzeImage: initialData.analyzeImage,
                    analyzeVideo: initialData.analyzeVideo,
                    analyzeAudio: initialData.analyzeAudio,
                    is_consumption_limit_enabled: initialData.is_consumption_limit_enabled,
                    max_coins_consumption: initialData.max_coins_consumption,
                    consumption_duration: initialData.consumption_duration,
                    snooze_duration: initialData.snooze_duration,
                    is_processing_delay_enabled: initialData.is_processing_delay_enabled,
                    processing_delay: initialData.processing_delay,
                    processing_jitter: initialData.processing_jitter,
                    admins: initialData.admin_contacts?.map(admin => admin.id) || [],
                } : {
                    name: '',
                    knowledgeBase: '',
                    type: 'messenger',
                    admins: [],
                    system_instruction: 'You are a customer service AI assistant created by Custom Answer, designed to help customers of this platform account.',
                }}
                onValuesChange={handleValuesChange}
                style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
                onFieldsChange={(changedFields) => {
                    // Check if knowledgeBase field has changed
                    if (changedFields.some(field => field.name[0] === 'knowledgeBase')) {
                        setSubmitDisabled(isButtonDisabled());
                    }
                }}
            >
                <div style={{ flex: 1 }}>
                    <Row align="bottom"
                        gutter={16}
                        style={{
                            overflowY: 'auto',
                            padding: '8px 0',
                            marginBottom: 16,
                        }}>
                        {steps.map((step, index) => (
                            <Fragment key={index}>
                                {currentStep === index && step.content}
                            </Fragment>
                        ))}
                    </Row>
                </div>
            </Form>
        </Modal>
    );
};

export default AddAgentModal;
